import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NOTIFY, getNotifyLevel, setNotifyLevel } from './notifications';

/**
 * Notification preferences modal.
 * Opened from the map's Subscribe button (autoSubscribe = true → opts the user
 * into daily updates immediately and confirms) or from the account menu
 * (autoSubscribe = false → just manage the existing setting).
 */
export default function NotificationsModal({ onClose, autoSubscribe = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [daily, setDaily] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const level = await getNotifyLevel();
      const isDaily = level === NOTIFY.DAILY;
      if (cancelled) return;
      if (autoSubscribe && !isDaily) {
        try {
          await setNotifyLevel(NOTIFY.DAILY);
          if (!cancelled) setDaily(true);
        } catch {
          if (!cancelled) {
            setDaily(false);
            setError('Could not update your subscription. Please try again.');
          }
        }
      } else {
        setDaily(isDaily);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [autoSubscribe]);

  const toggle = async () => {
    const next = !daily;
    setSaving(true);
    setError(null);
    setDaily(next);
    try {
      await setNotifyLevel(next ? NOTIFY.DAILY : NOTIFY.NONE);
    } catch {
      setDaily(!next);
      setError('Could not update your subscription. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-gray-700 bg-gray-800 p-8 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-2xl font-semibold text-white">Notifications</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 transition hover:text-white"
          >
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {loading ?
          <p className="py-6 text-center text-sm text-gray-400">Loading…</p>
        : <>
            {/* Setting row with toggle — turns green when subscribed */}
            <div
              className={
                'flex items-center justify-between gap-4 rounded-xl border px-4 py-4 transition ' +
                (daily ?
                  'border-emerald-500/40 bg-emerald-500/10'
                : 'border-gray-700 bg-gray-900/50')
              }
            >
              <div className="flex items-center gap-3">
                <span
                  className={
                    'flex h-10 w-10 items-center justify-center rounded-full transition ' +
                    (daily ?
                      'bg-emerald-500/20 text-emerald-400'
                    : 'bg-blue-600/15 text-blue-400')
                  }
                >
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                  </svg>
                </span>
                <div>
                  <p className="text-sm font-medium text-white">
                    Daily updates
                  </p>
                  <p
                    className={
                      'whitespace-nowrap text-xs ' +
                      (daily ? 'text-emerald-400/90' : 'text-gray-400')
                    }
                  >
                    {daily ?
                      'A daily map summary will be emailed to you.'
                    : 'One email per day with the latest map'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={daily}
                aria-label="Daily updates"
                disabled={saving}
                onClick={toggle}
                className={
                  'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition disabled:opacity-50 ' +
                  (daily ? 'bg-emerald-500' : 'bg-gray-600')
                }
              >
                <span
                  className={
                    'inline-block h-5 w-5 transform rounded-full bg-white shadow transition ' +
                    (daily ? 'translate-x-5' : 'translate-x-0.5')
                  }
                />
              </button>
            </div>

            {error && (
              <p className="mt-4 text-center text-sm text-red-400">{error}</p>
            )}

            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full rounded-xl bg-blue-600 py-3 font-medium text-white transition hover:bg-blue-700"
            >
              Done
            </button>
          </>
        }
      </div>
    </div>,
    document.body,
  );
}
